import CurrencyDevice, { CurrencyCallback } from './CurrencyDevice';
import { DeviceStatusFlags, KioDeviceStatus, RawKioDeviceStatus } from '../MonitoredDevice';
import logger from '../../helpers/logger';
import { isKioAppAvailable } from '../../helpers/kioApp';

export enum CurrencyEventType {
	StatusChanged = 1,
	CreditsChanged = 2,
	StackResultChanged = 3,
	DispensedByAmount = 4,
	DispensedByPosition = 5,
	DispensedByDenom = 6,
	DeviceReset = 7,
}

interface CreditsChangedData {
	Value: number;
	Currency: string;
}

enum StackResultData {
	Success = 0,
	Pending = 1,
	Failed = 2,
}

export type CurrencyEventData = CreditsChangedData | StackResultData | number | boolean;

const STATUS_THROTTLE_MS = 500;

export default class CurrencyAcceptor implements CurrencyDevice {
	private deviceName?: string;
	private lastStatus: KioDeviceStatus = { statusBits: DeviceStatusFlags.Ok, details: {} };
	private lastStatusCheck: number = 0;
	private initTimeout?: number;
	private onAmountChanged?: CurrencyCallback;
	private onStackingChanged?: (isStacking: boolean) => void;
	private validCurrencies: string[] = [];
	private isEnabled: boolean = false;
	private isTestMode: boolean = false;
	private credits: number = 0;
	private currency: string = 'USD';

	constructor(deviceName?: string) {
		this.deviceName = deviceName;
		window.OnKioCurrencyDevice = this.onKioCurrencyDevice.bind(this);
	}

	// Note: KioCurrencyAcceptor.GetStatusEx() is slow and blocking, so it must be throttled to avoid UI hanging.
	getStatus(): KioDeviceStatus {
		try {
			const now = new Date().getTime();
			if (this.lastStatus && now - this.lastStatusCheck < STATUS_THROTTLE_MS) return this.lastStatus;

			this.lastStatusCheck = now;
			const status: RawKioDeviceStatus = JSON.parse(window.KioCurrencyAcceptor.GetStatusEx(this.deviceName));
			this.lastStatus = { statusBits: status.Status, details: status.Details };

			return this.lastStatus;
		} catch (error) {
			if (isKioAppAvailable()) logger.logCashAcceptor.error({}, 'Failed to get status', error);
			return { statusBits: DeviceStatusFlags.ReadStatusQueryFailed, details: {} };
		}
	}

	setTestMode(isTestMode: boolean): void {
		this.isTestMode = isTestMode;
	}

	beginTransaction(onAmountChanged: CurrencyCallback, onStackingChanged?: (isStacking: boolean) => void, validCurrencies: string[] = ['USD']) {
		if (this.isEnabled) return;
		this.initTimeout = setTimeout(() => {
			this.onAmountChanged = onAmountChanged;
			this.onStackingChanged = onStackingChanged;
			this.validCurrencies = validCurrencies;
			window.KioCurrencyAcceptor.Enable(this.deviceName);
			this.isEnabled = true;
		}, 200);
	}

	async onKioCurrencyDevice(_devName: string, eventType: CurrencyEventType, eventData: CurrencyEventData) {
		if (!this.isEnabled) return;

		if (eventType === CurrencyEventType.CreditsChanged) {
			const creditsChangedData = eventData as CreditsChangedData;
			this.credits = creditsChangedData.Value;
			this.currency = creditsChangedData.Currency;

			if (creditsChangedData.Value === 0) return;

			if (this.validCurrencies.indexOf(creditsChangedData.Currency) === -1) {
				window.KioCurrencyAcceptor.Reject(this.deviceName);
			} else if (this.isTestMode) {
				this.onAmountChanged?.(this.credits, this.currency);
				window.KioCurrencyAcceptor.Reject(this.deviceName);
			} else {
				window.KioCurrencyAcceptor.Stack(this.deviceName);
				this.onStackingChanged?.(true);
			}
		}

		if (eventType === CurrencyEventType.StackResultChanged) {
			const result = eventData as StackResultData;
			if (result === StackResultData.Success) this.onAmountChanged?.(this.credits, this.currency);
			else if (result === StackResultData.Failed) await logger.logCashAcceptor.error({}, `Failed to stack bill`, '');
			if (result !== StackResultData.Pending) this.onStackingChanged?.(false);
		}
	}

	endTransaction() {
		clearTimeout(this.initTimeout); // Avoid rapid-fire begin/end.  This causes KW to throw an exception.
		if (!this.isEnabled) return;
		window.KioCurrencyAcceptor.Disable(this.deviceName);
		this.onAmountChanged = undefined;
		this.onStackingChanged = undefined;
		this.validCurrencies = [];
		this.isEnabled = false;
	}
}

export class CurrencyAcceptorStub implements CurrencyDevice {
	private interval: number = 0;

	getStatus(): KioDeviceStatus {
		return { statusBits: DeviceStatusFlags.Ok, details: {} };
	}

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	setTestMode(_isTestMode: boolean): void {}

	beginTransaction(onAmountChanged: CurrencyCallback, onStackingChanged?: (isStacking: boolean) => void) {
		if (this.interval !== 0) return;

		const denominations = [1, 5, 10, 20];
		this.interval = setInterval(() => {
			const amount = denominations[Math.floor(Math.random() * denominations.length)];
			onStackingChanged?.(true);
			setTimeout(() => {
				onAmountChanged(amount, 'USD');
				onStackingChanged?.(false);
			}, 1000);
		}, 4000);
	}

	endTransaction() {
		if (this.interval === 0) return;
		clearInterval(this.interval);
		this.interval = 0;
	}
}
